import { createSelector } from 'reselect';

import { State } from './reducer';

export const getIsSidePanelOpen = ({ isSidePanelOpen }: State) => isSidePanelOpen;
export const getBasemapStyle = ({ basemapStyle }: State) => basemapStyle;

export const getInternalMowing = ({ internalMowing }: State) => internalMowing;
export const getPublicMowing = ({ publicMowing }: State) => publicMowing;
export const getSectionals = ({ sectionals }: State) => sectionals;
export const getTobinMaps = ({ tobinMaps }: State) => tobinMaps;
export const getOwnership = ({ ownership }: State) => ownership;
export const getAbstracts = ({ abstracts }: State) => abstracts;
export const getBeaumontZoning = ({ beaumontZoning }: State) => beaumontZoning;
export const getCommissionerPrecincts = ({ commissionerPrecincts }: State) => commissionerPrecincts;
export const getDitches = ({ ditches }: State) => ditches;
export const getDrainageDistricts = ({ drainageDistricts }: State) => drainageDistricts;
export const getParcels = ({ parcels }: State) => parcels;
export const getSubwatersheds = ({ subwatersheds }: State) => subwatersheds;
export const getWatersheds = ({ watersheds }: State) => watersheds;
export const getCounties = ({ counties }: State) => counties;
export const getSchoolDistricts = ({ schoolDistricts }: State) => schoolDistricts;
export const getSubdivisions = ({ subdivisions }: State) => subdivisions;

export const getIdentifiedLayers = ({ identify }: State) => identify;

export const getSearchLocation = ({ searchLocation }: State) => searchLocation;

export const getIsDarkMap = createSelector(getBasemapStyle, (basemapStyle) => {
  return basemapStyle === 'satellite' ? true : false;
});

export const getSelectedLayerId = createSelector(getIdentifiedLayers, (identify) => {
  return identify.selectedId;
});

export const getInternalConditionalInteractiveLayers = createSelector(
  getInternalMowing,
  (mowing) => {
    return mowing.features ? ['mowing-line'] : [];
  }
);

export const getPublicConditionalInteractiveLayers = createSelector(getPublicMowing, (mowing) => {
  return mowing.features ? ['mowing-line'] : [];
});

export const getSelectedEntities = createSelector(getIdentifiedLayers, (identify) => {
  return identify.entities;
});

export const getInternalMowingVisibility = createSelector(getInternalMowing, (mowing) => {
  return mowing.isVisible;
});

export const getInternalMowingFeatures = createSelector(getInternalMowing, (mowing) => {
  return mowing.features;
});

export const getInternalMowingStartDate = createSelector(getInternalMowing, (mowing) => {
  return mowing.startDate;
});

export const getInternalMowingEndDate = createSelector(getInternalMowing, (mowing) => {
  return mowing.endDate;
});

export const getPublicMowingFeatures = createSelector(getPublicMowing, (mowing) => {
  return mowing.features;
});

export const getSectionalsVisibility = createSelector(getSectionals, (sectionals) => {
  return sectionals.isVisible;
});

export const getTobinMapsVisibility = createSelector(getTobinMaps, (tobinMaps) => {
  return tobinMaps.isVisible;
});

export const getAbstractsVisibility = createSelector(getAbstracts, (abstracts) => {
  return abstracts.isVisible;
});

export const getDitchesVisibility = createSelector(getDitches, (ditches) => {
  return ditches.isVisible;
});
export const getBeaumontZoningVisibility = createSelector(getBeaumontZoning, (beaumontZoning) => {
  return beaumontZoning.isVisible;
});
export const getCommissionerPrecinctsVisibility = createSelector(
  getCommissionerPrecincts,
  (commissionerPrecincts) => {
    return commissionerPrecincts.isVisible;
  }
);
export const getDrainageDistrictsVisibility = createSelector(
  getDrainageDistricts,
  (drainageDistricts) => {
    return drainageDistricts.isVisible;
  }
);
export const getParcelsVisibility = createSelector(getParcels, (parcels) => parcels.isVisible);
export const getSubwatershedsVisibility = createSelector(getSubwatersheds, (subwatersheds) => {
  return subwatersheds.isVisible;
});
export const getWatershedsVisibility = createSelector(getWatersheds, (watersheds) => {
  return watersheds.isVisible;
});
export const getCountiesVisibility = createSelector(getCounties, (counties) => {
  return counties.isVisible;
});
export const getSchoolDistrictsVisibility = createSelector(
  getSchoolDistricts,
  (schoolDistricts) => {
    return schoolDistricts.isVisible;
  }
);
export const getSubdivisionsVisibility = createSelector(getSubdivisions, (subdivisions) => {
  return subdivisions.isVisible;
});

export const getOwnershipVisibility = createSelector(getOwnership, (ownership) => {
  return ownership.isVisible;
});

export const getVisibleDitchCategoryTypes = createSelector(getDitches, (ditches) => {
  const updatedCategoryTypes = ditches.categoryTypes.reduce(
    (arr: string[], categoryType) => {
      if (categoryType.isVisible) {
        return [...arr, categoryType.name];
      }
      return arr;
    },
    ['none']
  );
  return updatedCategoryTypes;
});

export const getVisibleOwnershipCategoryTypes = createSelector(getOwnership, (ownership) => {
  const updatedCategoryTypes = ownership.categoryTypes.reduce(
    (arr: string[], categoryType) => {
      if (categoryType.isVisible) {
        return [...arr, categoryType.name];
      }
      return arr;
    },
    ['none']
  );
  return updatedCategoryTypes;
});

export const getHighlightedLayer = createSelector(
  getSelectedEntities,
  getSelectedLayerId,
  (entities, selectedLayerId) => {
    if (!selectedLayerId) {
      return null;
    }
    const layerGeometry = entities.reduce((arr: any, entity: any) => {
      if (entity.id === selectedLayerId) {
        return [...arr, entity.geometry];
      }
      return arr;
    }, []);
    if (layerGeometry.length !== 1) {
      return null;
    }
    return layerGeometry[0];
  }
);
